import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { Html, Line } from "@react-three/drei";
import { crossSectionContour } from "./cross-section";

// the measured sizes are in millimetres.
const UNIT = "mm";
// the theme colour, for the value labels.
const ACCENT = "#43A047";
// the lines are a brighter green, to stand out against the blue mesh.
const LINE_COLOR = "#00E676";

// line thicknesses, as a fraction of the measured width.
const DIMENSION_WEIGHT = 0.012;
const OUTLINE_WEIGHT = 0.007;
// a measured span is not flat on the sole plane, so it is drawn as a line of a
// fixed on-screen thickness (in pixels), facing the camera from every angle.
const SPAN_LINE_WIDTH = 1.5;
const CONTOUR_LINE_WIDTH = 2;
// half length (in mm) of the crosses closing a span: the same for every span.
const SPAN_TICK = 5;

// "299.3808…" -> "299.4 mm"
export function formatMeasurement(value, unit = UNIT) {
  return typeof value === "number" ? `${value.toFixed(1)} ${unit}` : "—";
}

// The footprint annotation is drawn out of flat triangles rather than with
// drei's <Line>: the whole thing lies in one plane, so every line is just a
// thin strip, and this way the drawn shape is exactly the vertices written
// here.

// append the two triangles of a `weight` wide strip running from (x1,z1) to
// (x2,z2) in the y plane.
function strip(out, x1, z1, x2, z2, y, weight) {
  const dx = x2 - x1;
  const dz = z2 - z1;
  const span = Math.hypot(dx, dz);
  if (!span) return;
  // half the strip's thickness, perpendicular to its direction
  const ox = (-dz / span) * (weight / 2);
  const oz = (dx / span) * (weight / 2);
  out.push(
    x1 - ox,
    y,
    z1 - oz,
    x2 - ox,
    y,
    z2 - oz,
    x2 + ox,
    y,
    z2 + oz,

    x1 - ox,
    y,
    z1 - oz,
    x2 + ox,
    y,
    z2 + oz,
    x1 + ox,
    y,
    z1 + oz,
  );
}

// the same strip, broken into `dash` long dashes separated by `dash` long gaps.
function dashedStrip(out, x1, z1, x2, z2, y, weight, dash) {
  const dx = x2 - x1;
  const dz = z2 - z1;
  const span = Math.hypot(dx, dz);
  if (!(dash > 0)) {
    strip(out, x1, z1, x2, z2, y, weight);
    return;
  }
  for (let at = 0; at < span; at += dash * 2) {
    const to = Math.min(at + dash, span);
    strip(
      out,
      x1 + (dx * at) / span,
      z1 + (dz * at) / span,
      x1 + (dx * to) / span,
      z1 + (dz * to) / span,
      y,
      weight,
    );
  }
}

function geometryOf(vertices) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  return geometry;
}

// The measured footprint and the two dimension lines, all lying flat on the
// sole plane (box.yMin — the box's yMax is not used).
// 'labelSide' - which side of the footprint (along X, -1 or +1) the width
// label goes: away from the other foot when two are measured together.
function footprintDrawing(box, labelSide = 1) {
  const { minX, maxX, minZ, maxZ, yMin } = box;
  const length = maxZ - minZ;
  const width = maxX - minX;
  // the dimension lines cross at the footprint's centre
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const y = yMin || 0; // the sole plane
  const tick = width * 0.12; // half length of the ticks closing a dimension
  const pad = length * 0.06; // gap between a dimension line and its label
  const dash = length * 0.02;

  // the four edges of the measured footprint
  const outline = [];
  const thin = width * OUTLINE_WEIGHT;
  dashedStrip(outline, minX, minZ, maxX, minZ, y, thin, dash);
  dashedStrip(outline, maxX, minZ, maxX, maxZ, y, thin, dash);
  dashedStrip(outline, maxX, maxZ, minX, maxZ, y, thin, dash);
  dashedStrip(outline, minX, maxZ, minX, minZ, y, thin, dash);

  // the length (along Z) and the width (along X), each closed by a tick across
  // both of its ends
  const dimensions = [];
  const thick = width * DIMENSION_WEIGHT;
  strip(dimensions, cx, minZ, cx, maxZ, y, thick);
  strip(dimensions, cx - tick, minZ, cx + tick, minZ, y, thick);
  strip(dimensions, cx - tick, maxZ, cx + tick, maxZ, y, thick);
  strip(dimensions, minX, cz, maxX, cz, y, thick);
  strip(dimensions, minX, cz - tick, minX, cz + tick, y, thick);
  strip(dimensions, maxX, cz - tick, maxX, cz + tick, y, thick);

  return {
    outline: geometryOf(outline),
    dimensions: geometryOf(dimensions),
    lengthLabel: [cx, y, maxZ + pad],
    widthLabel: [labelSide < 0 ? minX - pad : maxX + pad, y, cz],
  };
}

// whether `p` is a point: [x, y, z].
function isPoint(p) {
  return Array.isArray(p) && p.length === 3 && p.every(Number.isFinite);
}

// The dimension line of a span measured between the end points `a` and `b`
// (the instep height, which runs up from the sole): the line itself, closed at
// both ends by a pair of crossing ticks (along X and Z) so that one of them
// always shows, whatever side the model is seen from. Returns the line
// segments (6 numbers each) and where its label goes, just past `b`.
function spanDrawing(a, b) {
  const span = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  const tick = SPAN_TICK; // half length of the ticks
  const pad = span * 0.2; // gap between the line's end and its label
  const segments = [...a, ...b];
  [a, b].forEach(([x, y, z]) => {
    segments.push(x - tick, y, z, x + tick, y, z);
    segments.push(x, y, z - tick, x, y, z + tick);
  });
  // the label continues the line's direction, past its `b` end
  const label = b.map((v, i) => v + ((v - a[i]) / (span || 1)) * pad);
  return { segments, label };
}

// the centre (along X) of a foot's measured box, to tell the feet apart.
function centerX(box) {
  return (box.minX + box.maxX) / 2;
}

// Dimension lines drawn on the sole plane of the measured mesh, for each
// measured foot in `sides` ([{ side, measurements }]): the length runs along
// Z, the width along X, both taken from the foot's measurement `box`. The
// measurements with two end points (the instep height) get their own
// dimension line between those, and the girths the contour of the mesh cut
// by their plane (`geometry` is the measured mesh).
export const MeasurementAnnotations = ({ sides, geometry }) => {
  const measured = sides || [];
  // the feet with a box to draw
  const feet = measured
    .map((foot) => {
      const entries = foot.measurements || [];
      return {
        key: foot.side || "",
        box: (entries.find((m) => m && m.box) || {}).box,
        lengthValue: entries.find((m) => m && m.key === "footLen"),
        widthValue: entries.find((m) => m && m.key === "footWid"),
      };
    })
    .filter((foot) => foot.box);
  // the spans measured between two end points, of all the feet
  const spans = measured.flatMap((foot, i) =>
    (foot.measurements || [])
      .filter((m) => m && isPoint(m.a) && isPoint(m.b))
      .map((m) => ({ ...m, id: `${foot.side || i}-${m.key}` })),
  );

  // the girths: a cut plane and a point on the contour to measure
  const girths = measured.flatMap((foot, i) =>
    (foot.measurements || [])
      .filter((m) => m && m.plane && isPoint(m.plane.point) && isPoint(m.seed))
      .map((m) => ({ ...m, id: `${foot.side || i}-${m.key}` })),
  );

  return (
    <group>
      {feet.map((foot, i) => {
        // with two feet side by side, each width label goes on the outside,
        // clear of the other foot.
        const others = feet.filter((_, j) => j !== i);
        const labelSide = others.some(
          (other) => centerX(other.box) > centerX(foot.box),
        )
          ? -1
          : 1;
        return (
          <FootAnnotations
            key={foot.key || i}
            box={foot.box}
            labelSide={labelSide}
            lengthValue={foot.lengthValue}
            widthValue={foot.widthValue}
          />
        );
      })}
      {spans.map((m) => (
        <SpanAnnotation key={m.id} a={m.a} b={m.b} value={m.value} />
      ))}
      {geometry &&
        girths.map((m) => (
          <GirthAnnotation
            key={m.id}
            geometry={geometry}
            plane={m.plane}
            seed={m.seed}
            value={m.value}
          />
        ))}
    </group>
  );
};

MeasurementAnnotations.propTypes = {
  sides: PropTypes.array,
  geometry: PropTypes.object,
};

// one foot's footprint, dimension lines and value labels.
const FootAnnotations = ({ box, labelSide, lengthValue, widthValue }) => {
  const drawn = useMemo(
    () => footprintDrawing(box, labelSide),
    [box, labelSide],
  );

  useEffect(() => {
    return () => {
      drawn.outline.dispose();
      drawn.dimensions.dispose();
    };
  }, [drawn]);

  return (
    <group>
      {/* the measured footprint. Both are drawn on top of the mesh (depthTest
          off) so they stay visible from every angle. */}
      <mesh geometry={drawn.outline} renderOrder={10}>
        <meshBasicMaterial
          color={LINE_COLOR}
          side={THREE.DoubleSide}
          depthTest={false}
          toneMapped={false}
        />
      </mesh>

      {/* the length and the width */}
      <mesh geometry={drawn.dimensions} renderOrder={10}>
        <meshBasicMaterial
          color={LINE_COLOR}
          side={THREE.DoubleSide}
          depthTest={false}
          toneMapped={false}
        />
      </mesh>

      {lengthValue && (
        <MeasurementLabel
          position={drawn.lengthLabel}
          text={formatMeasurement(lengthValue.value)}
        />
      )}
      {widthValue && (
        <MeasurementLabel
          position={drawn.widthLabel}
          text={formatMeasurement(widthValue.value)}
        />
      )}
    </group>
  );
};

FootAnnotations.propTypes = {
  box: PropTypes.object.isRequired,
  labelSide: PropTypes.number.isRequired,
  lengthValue: PropTypes.object,
  widthValue: PropTypes.object,
};

// the dimension line and value label of a span measured between two points.
const SpanAnnotation = ({ a, b, value }) => {
  const drawn = useMemo(() => spanDrawing(a, b), [a, b]);

  return (
    <group>
      {/* drawn on top of the mesh (depthTest off): the instep height runs
          through the inside of the foot. */}
      <Line
        segments
        points={drawn.segments}
        color={LINE_COLOR}
        lineWidth={SPAN_LINE_WIDTH}
        depthTest={false}
        toneMapped={false}
        renderOrder={10}
      />
      <MeasurementLabel
        position={drawn.label}
        text={formatMeasurement(value)}
      />
    </group>
  );
};

SpanAnnotation.propTypes = {
  a: PropTypes.array.isRequired,
  b: PropTypes.array.isRequired,
  value: PropTypes.number,
};

// the contour of the cross-section of the mesh at a girth's cut plane, and
// its value label.
const GirthAnnotation = ({ geometry, plane, seed, value }) => {
  const contour = useMemo(
    () => crossSectionContour(geometry, plane, seed),
    [geometry, plane, seed],
  );
  if (!contour.length) return null;

  return (
    <group>
      <Line
        segments
        points={contour}
        color={LINE_COLOR}
        lineWidth={CONTOUR_LINE_WIDTH}
        depthTest={false}
        toneMapped={false}
        renderOrder={10}
      />
      <MeasurementLabel position={seed} text={formatMeasurement(value)} />
    </group>
  );
};

GirthAnnotation.propTypes = {
  geometry: PropTypes.object.isRequired,
  plane: PropTypes.object.isRequired,
  seed: PropTypes.array.isRequired,
  value: PropTypes.number,
};

// a value label pinned to a point of the model.
const MeasurementLabel = ({ position, text }) => (
  <Html
    position={position}
    center
    // stay under the viewer's own controls
    zIndexRange={[10, 0]}
    style={{ pointerEvents: "none" }}
  >
    <div
      style={{
        backgroundColor: "#fff",
        border: `1px solid ${ACCENT}`,
        borderRadius: 12,
        color: ACCENT,
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.4,
        padding: "1px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </div>
  </Html>
);

MeasurementLabel.propTypes = {
  position: PropTypes.array.isRequired,
  text: PropTypes.string.isRequired,
};
